import { useQuery } from '@tanstack/react-query';

import { callService } from '@/services/api';
import { useStore } from '@/store/store';

/**
 * Hook to check if the Calls page should be shown in the sidebar.
 * Returns true only when both conditions are met:
 * 1. The selected program has a 'pqc_ingestion' workflow
 * 2. The user has at least one call in this program
 */
export const useShouldShowCallsPage = (): { shouldShow: boolean; isLoading: boolean } => {
  const { getWorkflowsArray, selectedProgramId } = useStore();
  const workflows = getWorkflowsArray();

  const hasPqcIngestion =
    workflows?.some(workflow => workflow.workflow_type === 'pqc_ingestion') ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['shouldShowCallsPage', selectedProgramId],
    queryFn: async () => {
      const response = await callService.getMyUserCalls({
        limit: 1,
        ...(selectedProgramId ? { program_id: selectedProgramId } : {}),
      });
      return (response.calls?.length ?? 0) > 0;
    },
    enabled: hasPqcIngestion && !!selectedProgramId,
    staleTime: 5 * 60 * 1000,
  });

  if (!hasPqcIngestion) {
    return { shouldShow: false, isLoading: false };
  }

  return { shouldShow: data ?? false, isLoading };
};
